// src/components/QuickLookChart.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchSeriesServer } from "../utils/fetchers";
import { computeRSI } from "../utils/indicators";

/* ------------------------- helpers ------------------------- */
const nf = (v, d = 4) =>
  v == null || !Number.isFinite(v)
    ? "—"
    : v.toLocaleString(undefined, { maximumFractionDigits: d });

const df = (ms) =>
  new Date(ms).toLocaleDateString(undefined, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });

const PRESETS = [
  { key: "1D",  label: "1D",  range: "1d",  interval: "5m"  },
  { key: "1W",  label: "1W",  range: "5d",  interval: "15m" },
  { key: "1M",  label: "1M",  range: "1mo", interval: "1h"  },
  // NOTE: YTD uses the 1Y cache; range/interval here are not used for fetching.
  { key: "YTD", label: "YTD", range: "ytd", interval: "1d"  },
  { key: "1Y",  label: "1Y",  range: "1y",  interval: "1d"  },
  { key: "5Y",  label: "5Y",  range: "5y",  interval: "1wk" },
  { key: "MAX", label: "MAX", range: "max", interval: "1mo" },
];

const DEFAULT_KEY = "1Y";

// easings
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);


// right gutter estimator
function estimateRightPad(labels, fontSize = 10) {
  const avgChar = fontSize * 0.62;
  const maxChars = Math.max(1, ...labels.map((s) => String(s).length));
  return Math.max(62, Math.ceil(maxChars * avgChar) + 14);
}

function domainYFor(series) {
  if (!series?.length) return [0, 1];
  let min = Infinity, max = -Infinity;
  for (const p of series) {
    if (p[1] == null) continue;
    if (p[1] < min) min = p[1];
    if (p[1] > max) max = p[1];
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    const v = Number.isFinite(max) ? max || 1 : 1;
    return [0, v];
  }
  return [min, max];
}

function makeScale(series, w, h, pad, domainY, domainX) {
  const xs = series.map((p) => p[0]);
  const minX = domainX ? domainX[0] : Math.min(...xs);
  const maxX = domainX ? domainX[1] : Math.max(...xs);
  const [minY, maxY] = domainY;

  const innerW = Math.max(1, w - pad.l - pad.r);
  const innerH = Math.max(1, h - pad.t - pad.b);
  const ix = (x) => pad.l + ((x - minX) * innerW) / (maxX - minX || 1);
  const iy = (y) => pad.t + ((maxY - y) * innerH) / (maxY - minY || 1);
  return { ix, iy, minX, maxX, minY, maxY, pad, innerW, innerH };
}

function pathFromSeries(points, ix, iy) {
  if (!points.length) return "";
  let d = `M ${ix(points[0][0])} ${iy(points[0][1])}`;
  for (let i = 1; i < points.length; i++) d += ` L ${ix(points[i][0])} ${iy(points[i][1])}`;
  return d;
}

const spanMs = (arr) => (!arr?.length ? 0 : arr[arr.length - 1][0] - arr[0][0]);

// piecewise-linear y(x) on sorted [[x,y],...]
function makeValueAt(series) {
  const n = series.length;
  if (!n) return () => null;
  return (x) => {
    if (x <= series[0][0]) return series[0][1];
    if (x >= series[n - 1][0]) return series[n - 1][1];
    let lo = 0, hi = n - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const xm = series[mid][0];
      if (xm === x) return series[mid][1];
      if (xm < x) lo = mid + 1;
      else hi = mid - 1;
    }
    const i = Math.max(1, lo);
    const x0 = series[i - 1][0], y0 = series[i - 1][1];
    const x1 = series[i][0],     y1 = series[i][1];
    const t = (x - x0) / (x1 - x0 || 1);
    return y0 + (y1 - y0) * t;
  };
}

function clipToWindow(series, x0, x1) {
  if (!series?.length) return [];
  const out = [];
  for (const p of series) if (p[0] >= x0 && p[0] <= x1) out.push(p);
  return out.length ? out : series.slice(-2);
}

// YTD helpers
function ytdStartFor(msEnd) {
  const d = new Date(msEnd);
  return new Date(d.getFullYear(), 0, 1).getTime();
}
function mapKeyForCache(key) {
  // YTD shares the 1Y cache
  return key === "YTD" ? "1Y" : key;
}

/* ------------------------- component ------------------------- */
export default function QuickLookChart({
  symbol,
  width = 880,
  height = 560,
  titleHeight = 44,
}) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [timeKey, setTimeKey] = useState(DEFAULT_KEY);
  const [series, setSeries] = useState([]);  // committed price
  const [rsi, setRsi] = useState([]);        // committed rsi

  const cacheRef = useRef(new Map());        // key -> { series, rsi }  (YTD uses '1Y')

  const [hoverIdx, setHoverIdx] = useState(null);

  // animation state (morph + camera + precomputed Y domains)
  const [anim, setAnim] = useState(null);
  const rafRef = useRef(null);

  /**
   * anim = {
   *   active, t0, dur,
   *   dir: 'in' | 'out',
   *   toKey,
   *   from: { s, rsi, x0, x1, valAt, valAtRSI },
   *   to:   { s, rsi, x0, x1, valAt, valAtRSI },
   *   yFrom: [minY, maxY],
   *   yTo:   [minY, maxY],
   * }
   */

  // initial load for symbol (fetch default range; if default is YTD, fetch 1Y)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const fetchKey = mapKeyForCache(timeKey);
        const preset = PRESETS.find((p) => p.key === fetchKey) || PRESETS[0];
        const json = await fetchSeriesServer(symbol, {
          range: preset.range,
          interval: preset.interval,
        });
        const closes = json.series.map((p) => p[1]);
        const r = computeRSI(closes, 14);
        cacheRef.current.set(fetchKey, { series: json.series, rsi: r });
        if (!mounted) return;
        setSeries(json.series);
        setRsi(r);
      } catch (e) {
        if (mounted) setErr(String(e?.message || "failed"));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  /* ------------------------- sizes & dynamic gutter ------------------------- */
  const priceH = Math.round((height - titleHeight) * 0.68);
  const rsiH = height - titleHeight - priceH;

  const priceMin = series.length ? Math.min(...series.map((p) => p[1])) : 0;
  const priceMax = series.length ? Math.max(...series.map((p) => p[1])) : 1;

  const gridY = 5;
  const priceLabelsPreview = Array.from({ length: gridY + 1 }, (_, i) => {
    const yVal = priceMin + (i * (priceMax - priceMin)) / gridY;
    return yVal == null || !Number.isFinite(yVal)
      ? "—"
      : yVal.toLocaleString(undefined, { maximumFractionDigits: 4 });
  });
  const pricePadRight = estimateRightPad(priceLabelsPreview, 10);

  /* ------------------------- animation tick ------------------------- */
  useEffect(() => {
    if (!anim?.active) return;
    cancelAnimationFrame(rafRef.current);
    const tick = () => {
      setAnim((prev) => (prev && prev.active ? { ...prev } : prev));
      const t = (performance.now() - anim.t0) / anim.dur;
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setSeries(anim.to.s);
        setRsi(anim.to.rsi);
        setTimeKey(anim.toKey);
        setAnim(null);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [anim]);

  /* ------------------------- render-time blended series ------------------------- */
  const SAMPLES = Math.min(900, Math.max(260, Math.floor(width)));

  const blended = useMemo(() => {
    // Helper to compute current static view window (YTD clips to Jan 1, others = full range)
    const computeStaticXWindow = (S) => {
      if (!S?.length) return { x0: 0, x1: 1 };
      const xs = S.map((p) => p[0]);
      const x1 = Math.max(...xs);
      const x0 = timeKey === "YTD" ? Math.max(ytdStartFor(x1), Math.min(...xs)) : Math.min(...xs);
      return { x0, x1 };
    };

    if (!anim?.active) {
      const { x0, x1 } = computeStaticXWindow(series);
      const clippedPrice = clipToWindow(series, x0, x1);
      const clippedRSI = series
        .map((p, i) => [p[0], rsi[i] ?? null])
        .filter((p) => p[1] != null && p[0] >= x0 && p[0] <= x1);
      return {
        price: clippedPrice,
        rsi: clippedRSI,
        x0, x1,
      };
    }

    const raw = Math.min(1, (performance.now() - anim.t0) / anim.dur);

    // domain ("camera") timing
    const tCam = easeInOut(raw);
    const wMorph = tCam; // morph in lockstep with zoom

    // right-anchored X window interpolation
    const x1 = anim.from.x1;
    const x0 = anim.from.x0 + (anim.to.x0 - anim.from.x0) * tCam;

    // build shared X grid & blend Y ON THAT GRID
    const outPrice = new Array(SAMPLES);
    const outRSI = [];

    for (let i = 0; i < SAMPLES; i++) {
      const u = i / (SAMPLES - 1);
      const x = x0 + (x1 - x0) * u;

      const ya = anim.from.valAt(x);
      const yb = anim.to.valAt(x);
      const y = ya + (yb - ya) * wMorph;

      const ra = anim.from.valAtRSI(x);
      const rb = anim.to.valAtRSI(x);
      const rBlend =
        ra != null && rb != null ? ra + (rb - ra) * wMorph : (ra != null ? ra : rb);

      outPrice[i] = [x, y];
      if (rBlend != null) outRSI.push([x, rBlend]);
    }

    return { price: outPrice, rsi: outRSI, x0, x1, raw };
  }, [anim, series, rsi, SAMPLES, timeKey]);

  /* ------------------------- Y-domain interpolation ------------------------- */
  const priceDomainY = useMemo(() => {
    if (!anim?.active) {
      const [minY, maxY] = domainYFor(blended.price);
      const pad = (maxY - minY) * 0.06 || 1;
      return [minY - pad * 0.3, maxY + pad];
    }

    const raw = Math.min(1, (performance.now() - anim.t0) / anim.dur);
    const yEase = easeInOut(raw);

    const [y0a, y1a] = anim.yFrom;
    const [y0b, y1b] = anim.yTo;
    const lerp = (a, b) => a + (b - a) * yEase;
    return [lerp(y0a, y0b), lerp(y1a, y1b)];
  }, [anim, blended]);

  /* ------------------------- scales & paths ------------------------- */
  const priceScale = useMemo(() => {
    return makeScale(
      blended.price.length ? blended.price : [[0, 0], [1, 1]],
      width,
      priceH,
      { l: 12, r: pricePadRight, t: 10, b: 18 },
      priceDomainY,
      [blended.x0, blended.x1]
    );
  }, [blended.price, blended.x0, blended.x1, width, priceH, pricePadRight, priceDomainY]);

  const pricePath = useMemo(
    () => pathFromSeries(blended.price, priceScale.ix, priceScale.iy),
    [blended.price, priceScale]
  );

  const rsiScale = useMemo(() => {
    return makeScale(
      blended.rsi.length ? blended.rsi : [[0, 0], [1, 100]],
      width,
      rsiH,
      { l: 12, r: 62, t: 14, b: 36 },
      [-2, 102],
      [blended.x0, blended.x1]
    );
  }, [blended.rsi, blended.x0, blended.x1, width, rsiH]);

  const rsiPath = useMemo(
    () => pathFromSeries(blended.rsi, rsiScale.ix, rsiScale.iy),
    [blended.rsi, rsiScale]
  );

  // grid (price)
  const priceGrid = useMemo(() => {
    const out = [];
    for (let i = 0; i <= gridY; i++) {
      const yVal = priceScale.minY + (i * (priceScale.maxY - priceScale.minY)) / gridY;
      out.push({ yVal, yPix: priceScale.iy(yVal) });
    }
    return out;
  }, [priceScale]);

  /* ------------------------- hover ------------------------- */
  const handlePointer = (e, scale, data) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const xPix = e.clientX - rect.left;
    const ms = scale.minX + ((xPix - scale.pad.l) * (scale.maxX - scale.minX)) / (scale.innerW || 1);
    const arr = data;
    let lo = 0, hi = arr.length - 1, best = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (arr[mid][0] < ms) { best = mid; lo = mid + 1; } else hi = mid - 1;
    }
    setHoverIdx(best);
  };

  const clearHover = () => setHoverIdx(null);
  const hRSI = hoverIdx != null && blended.rsi.length ? blended.rsi[Math.min(hoverIdx, blended.rsi.length - 1)][1] : null;

  /* ------------------------- range switching (YTD shares 1Y cache) ------------------------- */
  const switchRange = async (key) => {
    if (key === timeKey || !symbol) return;

    const curCacheKey = mapKeyForCache(timeKey);
    const tgtCacheKey = mapKeyForCache(key);

    const current = cacheRef.current.get(curCacheKey) ?? { series, rsi };
    let target = cacheRef.current.get(tgtCacheKey);

    // ensure target loaded (YTD will fetch 1Y if not cached)
    if (!target) {
      try {
        setLoading(true);
        const preset = PRESETS.find((p) => p.key === tgtCacheKey) || PRESETS[0];
        const json = await fetchSeriesServer(symbol, {
          range: preset.range,
          interval: preset.interval,
        });
        const closes = json.series.map((p) => p[1]);
        target = { series: json.series, rsi: computeRSI(closes, 14) };
        cacheRef.current.set(tgtCacheKey, target);
      } catch (e) {
        setErr(String(e?.message || "failed"));
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    const curS = current.series, curR = current.rsi;
    const tgtS = target.series,  tgtR = target.rsi;

    // right-anchored last timestamp shared by both series
    const curLast = curS[curS.length - 1][0];
    const tgtLast = tgtS[tgtS.length - 1][0];
    const rightAnchor = Math.min(curLast, tgtLast);
    const jan1 = ytdStartFor(rightAnchor);

    // FROM window reflects what is currently *visible*
    const fromX1 = rightAnchor;
    const fromX0 = (timeKey === "YTD")
      ? Math.max(jan1, curS[0][0])
      : rightAnchor - spanMs(curS);

    // TO window reflects target tab
    const toX1 = rightAnchor;
    const toX0 = (key === "YTD")
      ? Math.max(jan1, tgtS[0][0])
      : rightAnchor - spanMs(tgtS);

    const from = {
      s: curS,
      rsi: curR,
      x0: fromX0,
      x1: fromX1,
      valAt: makeValueAt(curS),
      valAtRSI: makeValueAt(curS.map((p, i) => [p[0], curR[i] ?? null]).filter((p) => p[1] != null)),
    };

    const to = {
      s: tgtS,
      rsi: tgtR,
      x0: toX0,
      x1: toX1,
      valAt: makeValueAt(tgtS),
      valAtRSI: makeValueAt(tgtS.map((p, i) => [p[0], tgtR[i] ?? null]).filter((p) => p[1] != null)),
    };

    const curSpanEff = fromX1 - fromX0;
    const tgtSpanEff = toX1 - toX0;
    const dir = tgtSpanEff < curSpanEff ? "in" : "out";

    // precompute Y endpoints (with padding) for smooth, monotonic scaling
    const startWin = clipToWindow(curS, fromX0, fromX1);
    const endWin   = clipToWindow(tgtS, toX0, toX1);

    const padY = (minY, maxY) => {
      const pad = (maxY - minY) * 0.06 || 1;
      return [minY - pad * 0.3, maxY + pad];
    };

    const yFrom = padY(...domainYFor(startWin));
    const yTo   = padY(...domainYFor(endWin));

    // duration scales mildly with jump size
    const ratio = Math.max(curSpanEff, tgtSpanEff) / Math.max(1, Math.min(curSpanEff, tgtSpanEff));
    const dur = Math.max(420, Math.min(900, 440 + Math.log2(ratio + 1) * 220));

    cancelAnimationFrame(rafRef.current);
    setAnim({
      active: true,
      t0: performance.now(),
      dur,
      dir,
      toKey: key,
      from,
      to,
      yFrom,
      yTo,
    });
  };

  /* ------------------------- UI ------------------------- */
  const clipIdPrice = "clip-price";
  const clipIdRSI = "clip-rsi";

  return (
    <div style={{ width: "100%", height: "100%", color: "#ddd" }}>
      {/* Title / status */}
      <div
        style={{
          height: titleHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 12px",
          borderBottom: "1px solid #1f1f1f",
          fontSize: 12,
          gap: 8,
        }}
      >
        <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
          <span>{symbol}</span>

          {/* Time range control */}
          <div
            role="tablist"
            aria-label="Time range"
            style={{
              display: "inline-flex",
              gap: 6,
              background: "#121212",
              padding: 4,
              borderRadius: 12,
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
            }}
          >
            {PRESETS.map((p) => {
              const selected = timeKey === p.key && !anim?.active;
              const pending = anim?.active && p.key === anim?.toKey;
              return (
                <button
                  key={p.key}
                  onClick={() => switchRange(p.key)}
                  role="tab"
                  aria-selected={selected || pending}
                  style={{
                    padding: "6px 8px",
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 800,
                    fontSize: 11,
                    background: selected || pending ? "#1f1f1f" : "transparent",
                    color: selected || pending ? "#fff" : "#c7c7c7",
                  }}
                  disabled={loading || anim?.active || p.key === timeKey}
                  title={`Switch to ${p.label}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          {hoverIdx != null && blended.price.length ? (
            <>
              <b style={{ marginRight: 8 }}>{df(blended.price[hoverIdx][0])}</b>
              Price {nf(blended.price[hoverIdx][1], 2)}
              {hRSI == null ? null : <> • RSI {nf(hRSI, 1)}</>}
            </>
          ) : loading ? (
            "Loading…"
          ) : err ? (
            <span style={{ color: "#f66" }}>{err}</span>
          ) : anim?.active ? (
            "Morphing…"
          ) : (
            "Hover the chart"
          )}
        </div>
      </div>

      {/* PRICE */}
      <svg
        width={width}
        height={priceH}
        onPointerMove={(e) => handlePointer(e, priceScale, blended.price)}
        onPointerLeave={clearHover}
        style={{ display: "block", background: "#0f0f0f", touchAction: "none" }}
      >
        <defs>
          <clipPath id={clipIdPrice}>
            <rect
              x={priceScale.pad.l}
              y={priceScale.pad.t - 1}
              width={priceScale.innerW}
              height={priceScale.innerH + 2}
            />
          </clipPath>
        </defs>

        {/* grid + labels */}
        {priceGrid.map((g, i) => (
          <g key={i}>
            <line
              x1={priceScale.pad.l}
              x2={width - priceScale.pad.r}
              y1={g.yPix}
              y2={g.yPix}
              stroke="#1e1e1e"
              strokeDasharray="3 3"
            />
            <text x={width - priceScale.pad.r + 4} y={g.yPix + 3} fontSize="10" fill="#888">
              {nf(g.yVal, 4)}
            </text>
          </g>
        ))}

        <g clipPath={`url(#${clipIdPrice})`}>
          <path d={pricePath} fill="none" stroke="#9bd" strokeWidth="1.8" />
          {hoverIdx != null && blended.price[hoverIdx] && (
            <>
              <line
                x1={priceScale.ix(blended.price[hoverIdx][0])}
                x2={priceScale.ix(blended.price[hoverIdx][0])}
                y1={priceScale.pad.t}
                y2={priceH - priceScale.pad.b}
                stroke="#333"
              />
              <circle
                cx={priceScale.ix(blended.price[hoverIdx][0])}
                cy={priceScale.iy(blended.price[hoverIdx][1])}
                r="3.2"
                fill="#9bd"
              />
            </>
          )}
        </g>
      </svg>

      {/* RSI */}
      <svg
        width={width}
        height={rsiH}
        onPointerMove={(e) => handlePointer(e, rsiScale, blended.rsi.length ? blended.rsi : blended.price)}
        onPointerLeave={clearHover}
        style={{ display: "block", borderTop: "1px solid #1a1a1a", background: "#0f0f0f", touchAction: "none" }}
      >
        <defs>
          <clipPath id={clipIdRSI}>
            <rect
              x={rsiScale.pad.l}
              y={rsiScale.pad.t - 1}
              width={rsiScale.innerW}
              height={rsiScale.innerH + 2}
            />
          </clipPath>
        </defs>

        {[0, 30, 50, 70, 100].map((v, i) => {
          const y = rsiScale.iy(v);
          return (
            <g key={i}>
              <line
                x1={rsiScale.pad.l}
                x2={width - rsiScale.pad.r}
                y1={y}
                y2={y}
                stroke={v === 50 ? "#2a2a2a" : "#1e1e1e"}
                strokeDasharray={v === 50 ? "" : "3 3"}
              />
              <text
                x={width - rsiScale.pad.r + 6}
                y={Math.min(rsiH - rsiScale.pad.b + 10, y + 3)}
                fontSize="10"
                fill="#888"
              >
                {v}
              </text>
            </g>
          );
        })}

        <g clipPath={`url(#${clipIdRSI})`}>
          <path d={rsiPath} fill="none" stroke="#c9c" strokeWidth="1.6" />
          {hoverIdx != null && hRSI != null && (
            <>
              <line
                x1={rsiScale.ix(blended.price[hoverIdx][0])}
                x2={rsiScale.ix(blended.price[hoverIdx][0])}
                y1={rsiScale.pad.t}
                y2={rsiH - rsiScale.pad.b}
                stroke="#333"
              />
              <circle
                cx={rsiScale.ix(blended.price[hoverIdx][0])}
                cy={rsiScale.iy(hRSI)}
                r="3.0"
                fill="#c9c"
              />
            </>
          )}
        </g>
      </svg>
    </div>
  );
}
